import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

/**
 * Theme switching (FINALIZING.md G-9.1, owner decisions 2026-07-26/27):
 * dark mode ships in v1; the preference follows the device by default and can
 * be overridden per device from ProfilePage — the shared floor tablet is
 * pinned by simply choosing "light" on it.
 *
 * Mechanics: preference ('light' | 'dark' | 'system') lives in
 * localStorage["fw-theme"]; the RESOLVED theme is stamped as
 * `data-theme="light|dark"` on <html>, which tokens.css keys its dark
 * overrides off. `initTheme()` runs before React mounts (main.tsx) so the
 * first paint is already themed — no flash.
 */

export type ThemePreference = "light" | "dark" | "system";

const STORAGE_KEY = "fw-theme";
const DARK_MQ = "(prefers-color-scheme: dark)";

/**
 * Hard theme lock (owner decision 2026-07-28): while set, EVERY stamp —
 * preference changes, live OS-scheme flips, anything — resolves to the locked
 * theme. The floor kiosk locks "light" for its whole lifetime (D-17: the
 * floor surface has no dark parity yet), so no system event can flip the
 * counter screen mid-shift.
 */
let themeLock: "light" | "dark" | null = null;

export function lockTheme(theme: "light" | "dark") {
  themeLock = theme;
  document.documentElement.setAttribute("data-theme", theme);
}

export function unlockTheme() {
  themeLock = null;
  stamp(storedPreference());
}

function storedPreference(): ThemePreference {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return raw === "light" || raw === "dark" || raw === "system" ? raw : "system";
}

function resolve(preference: ThemePreference): "light" | "dark" {
  if (preference === "system") {
    return window.matchMedia(DARK_MQ).matches ? "dark" : "light";
  }
  return preference;
}

function stamp(preference: ThemePreference) {
  document.documentElement.setAttribute(
    "data-theme",
    themeLock ?? resolve(preference),
  );
}

/** Call once before React renders (main.tsx) so the first paint is themed. */
export function initTheme() {
  stamp(storedPreference());
}

const ThemeContext = createContext<{
  preference: ThemePreference;
  setPreference: (next: ThemePreference) => void;
}>({ preference: "system", setPreference: () => undefined });

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    storedPreference(),
  );

  const setPreference = useCallback((next: ThemePreference) => {
    window.localStorage.setItem(STORAGE_KEY, next);
    setPreferenceState(next);
    stamp(next);
  }, []);

  // Live-update while the preference is "system" and the OS theme flips.
  useEffect(() => {
    if (preference !== "system") return;
    const mq = window.matchMedia(DARK_MQ);
    const apply = () => stamp("system");
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [preference]);

  return (
    <ThemeContext.Provider value={{ preference, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
